/**
 * The V0 violence slice (§17): fistfights and one shakedown path.
 *
 * Same loop as a conversation. A scene opens, beats resolve, the scene
 * exits into outcomes and an event. The sim resolves every beat from the
 * four inputs: participants (Body, Nerve, how many), context (his turf,
 * who's watching), precedent (surprise), intent. The model, when there is
 * one, narrates; here the beats come back as data.
 *
 * Intent is the field that matters. It starts where the player set it and
 * moves on its own: he swings first and a shakedown is a fight; he goes
 * down and your temper keeps your hands moving. What the event is, at the
 * end, is what it became.
 *
 * What this writes, through machinery that already exists:
 *  - the event, via `commitEvent`, so witnesses and the target remember it
 *    forever (§11) and a grievance is stored (§13; the ladder is not active);
 *  - injuries as states with a cost tail (`sim/injury.ts`);
 *  - relationship shifts and the shakedown's money as sim-sourced outcomes
 *    through `applyOutcomes`, under the same bounds a model contract gets.
 */

import { commitEvent } from './events.js';
import { makeInjury, severityFromBlows } from './injury.js';
import { effectiveStats } from './needs.js';
import { applyOutcomes } from './outcomes.js';
import { isPresent, npcsAt } from './presence.js';
import { Rng } from './rng.js';
import { TUNING } from './tuning.js';
import type {
  ActiveScene,
  BeatLine,
  Combatant,
  Footing,
  Intent,
  NPC,
  NpcId,
  Outcome,
  SceneEnd,
  Stats,
  ViolenceKind,
  ViolenceVerb,
  WorldState,
} from './types/index.js';

// ---------------------------------------------------------------------------
// Opening a scene
// ---------------------------------------------------------------------------

export interface StartSpec {
  readonly kind: ViolenceKind;
  readonly npc: NpcId;
  /** Shakedown: what you're asking for. */
  readonly demand?: number;
}

export function startScene(state: WorldState, spec: StartSpec): WorldState | string {
  if (state.scene !== null) return 'you are already in the middle of something';
  const npc = state.npcs[spec.npc];
  if (!npc) return 'no such person';
  if (npc.status !== 'present') return `${npc.name} is not around`;
  const here = state.player.position.at;
  if (here === null || !isPresent(state, spec.npc)) return `${npc.name} is not here`;
  if (spec.kind === 'shakedown') {
    if (!Number.isFinite(spec.demand) || (spec.demand ?? 0) <= 0) return 'name a number';
  }

  const rng = Rng.fromState(state.rng, state.seed).fork(`scene:${state.memory.events.length}`);
  const you = effectiveStats(state);
  const holdsGrudge = Object.values(state.grievances).some(
    (g) => g.polarity === 'grievance' && g.holder === spec.npc && g.against === 'player' && g.status !== 'settled',
  );
  // Precedent: did he see you coming. A man who already hates you is watching.
  const surprise = !holdsGrudge && npc.truth.mood.mood !== 'angry' && rng.bool(0.25 + 0.1 * (you.read - npc.stats.read));

  const scene: ActiveScene = {
    kind: spec.kind,
    npc: spec.npc,
    location: here,
    startedAt: state.time,
    intent: { player: spec.kind === 'shakedown' ? 'scare' : 'hurt', npc: 'scare' },
    startedAs: spec.kind === 'shakedown' ? 'scare' : 'hurt',
    demand: spec.kind === 'shakedown' ? Math.round(spec.demand!) : null,
    surprise,
    hisTurf: state.locations[here]?.owner === spec.npc,
    witnesses: npcsAt(state, here).filter((id) => id !== spec.npc),
    beat: 0,
    maxBeats: TUNING.violence.maxBeats,
    player: { footing: 'standing', blows: 0 },
    target: { footing: 'standing', blows: 0 },
    physical: false,
    pressure: 0,
    lines: [],
    budget: { relationship: {}, stance: {} },
  };
  return { ...state, scene, rng: rng.getState() };
}

// ---------------------------------------------------------------------------
// The verbs available now (§14 affordance list, swapped)
// ---------------------------------------------------------------------------

export function sceneVerbs(scene: ActiveScene): readonly ViolenceVerb[] {
  const over = scene.target.footing === 'down' || scene.target.footing === 'fled' || scene.target.footing === 'yielded';
  if (over) return [];
  const verbs: ViolenceVerb[] = [];
  if (scene.kind === 'shakedown' && !scene.physical) {
    verbs.push('press', 'demand', 'grab', 'swing', 'talk_down', 'let_go');
  } else {
    verbs.push('press', 'swing', 'grab', 'talk_down', 'back_off', 'run');
  }
  return verbs;
}

// ---------------------------------------------------------------------------
// One beat
// ---------------------------------------------------------------------------

export interface BeatOutcome {
  readonly state: WorldState;
  readonly lines: readonly BeatLine[];
  /** Set when this beat ended the scene. */
  readonly end: SceneEnd | null;
}

function clamp01(n: number): number {
  return Math.max(0.03, Math.min(0.97, n));
}

/** Nerve is whether you can do it at all; Body is whether it lands (§17). */
function hitChance(attacker: Stats, defender: Stats, surprise: boolean, defenderFooting: Footing): number {
  let p = 0.5 + 0.12 * (attacker.body - defender.body) + 0.05 * (attacker.nerve - defender.nerve);
  if (surprise) p += 0.25;
  if (defenderFooting === 'held' || defenderFooting === 'staggered') p += 0.15;
  return clamp01(p);
}

function afterBlow(c: Combatant): Combatant {
  const blows = c.blows + 1;
  const footing: Footing = blows >= TUNING.violence.blowsToDown ? 'down' : blows >= TUNING.violence.blowsToStagger ? 'staggered' : c.footing;
  return { blows, footing };
}

/** Presence against his fear and his options (§17 shakedown). Not a fight. */
function yieldChance(scene: ActiveScene, you: Stats, npc: NPC): number {
  // NPCs must be able to say no and mean it (§14): a hard man on his own
  // ground, facing someone who doesn't outclass him, does not pay.
  if (npc.stats.nerve >= 4 && scene.hisTurf && you.presence <= npc.stats.presence) return 0;
  const bravado = npc.flaw.kind === 'bravado' && scene.witnesses.length > 0 ? 1 : 0;
  const fear = npc.truth.relationship.fear / 25;
  const broke = scene.demand !== null && npc.truth.money < scene.demand ? 1 : 0;
  const edge = you.presence + fear + scene.pressure + (scene.surprise ? 0.5 : 0) - npc.stats.nerve - (scene.hisTurf ? 1 : 0) - bravado - broke;
  return clamp01(0.35 + 0.15 * edge);
}

export function resolveBeat(state: WorldState, verb: ViolenceVerb): BeatOutcome | string {
  const scene = state.scene;
  if (scene === null) return 'nothing is happening';
  if (!sceneVerbs(scene).includes(verb)) return `you can't ${verb.replace('_', ' ')} now`;
  const npc = state.npcs[scene.npc];
  if (!npc) return 'no such person';

  const rng = Rng.fromState(state.rng, state.seed);
  const you = effectiveStats(state);
  const him = npc.stats;
  const lines: BeatLine[] = [];
  let s: ActiveScene = { ...scene, beat: scene.beat + 1 };
  let ended = false;

  const escalate = (who: 'player' | 'npc', to: Intent, why: 'swung_first' | 'temper' | 'chose_it') => {
    const current = s.intent[who];
    if (current === to || (current === 'hurt' && to === 'scare')) return;
    s = { ...s, intent: { ...s.intent, [who]: to } };
    lines.push({ who, act: 'escalates', to, why });
  };

  const landOn = (who: 'player' | 'npc') => {
    if (who === 'npc') {
      s = { ...s, target: afterBlow(s.target), physical: true };
      lines.push({ who: 'player', act: 'swings_and_lands' });
      if (s.target.footing === 'down') lines.push({ who: 'npc', act: 'goes_down' });
      else if (s.target.footing === 'staggered') lines.push({ who: 'npc', act: 'staggers' });
    } else {
      s = { ...s, player: afterBlow(s.player), physical: true };
      lines.push({ who: 'npc', act: 'swings_and_lands' });
      if (s.player.footing === 'down') lines.push({ who: 'player', act: 'goes_down' });
      else if (s.player.footing === 'staggered') lines.push({ who: 'player', act: 'staggers' });
    }
  };

  // --- your move ------------------------------------------------------------
  switch (verb) {
    case 'swing': {
      escalate('player', 'hurt', 'chose_it');
      const first = s.beat === 1 && s.surprise;
      if (rng.bool(hitChance(you, him, first, s.target.footing))) landOn('npc');
      else lines.push({ who: 'player', act: 'swings_and_misses' });
      break;
    }
    case 'grab': {
      const p = clamp01(0.5 + 0.15 * (you.body - him.body) + (s.surprise && s.beat === 1 ? 0.2 : 0));
      if (rng.bool(p)) {
        s = { ...s, target: { ...s.target, footing: 'held' }, pressure: s.pressure + 1 };
        lines.push({ who: 'player', act: 'grabs' });
      } else {
        lines.push({ who: 'npc', act: 'shakes_free' });
      }
      break;
    }
    case 'press': {
      s = { ...s, pressure: s.pressure + 1 };
      lines.push({ who: 'player', act: 'presses' });
      const wavers = rng.bool(clamp01(0.3 + 0.15 * (you.presence + s.pressure - him.nerve)));
      lines.push({ who: 'npc', act: wavers ? 'wavers' : 'holds_ground' });
      if (wavers) s = { ...s, pressure: s.pressure + 1 };
      break;
    }
    case 'demand': {
      lines.push({ who: 'player', act: 'demands' });
      if (rng.bool(yieldChance(s, you, npc))) {
        const amount = Math.min(s.demand ?? 0, Math.floor(npc.truth.money));
        s = { ...s, target: { ...s.target, footing: 'yielded' } };
        lines.push({ who: 'npc', act: 'yields' }, { who: 'npc', act: 'pays', amount });
        ended = true;
      } else {
        lines.push({ who: 'npc', act: 'refuses' });
      }
      break;
    }
    case 'talk_down': {
      lines.push({ who: 'player', act: 'talks' });
      const p = clamp01(0.35 + 0.15 * (you.presence - him.nerve) + npc.truth.relationship.warmth / 50 - (s.physical ? 0.2 : 0));
      if (rng.bool(p)) {
        lines.push({ who: 'npc', act: 'listens' });
        s = { ...s, target: { ...s.target, footing: 'yielded' } };
        ended = true;
      } else {
        lines.push({ who: 'npc', act: 'refuses' });
      }
      break;
    }
    case 'back_off':
      lines.push({ who: 'player', act: 'backs_off' });
      ended = true;
      break;
    case 'let_go':
      lines.push({ who: 'player', act: 'lets_go' });
      ended = true;
      break;
    case 'run': {
      lines.push({ who: 'player', act: 'runs' });
      // A parting shot if he's on his feet and quicker than you.
      if (s.target.footing === 'standing' && rng.bool(clamp01(0.2 + 0.1 * (him.body - you.body)))) landOn('player');
      s = { ...s, player: { ...s.player, footing: 'fled' } };
      ended = true;
      break;
    }
  }

  // --- his move -------------------------------------------------------------
  if (!ended && s.target.footing !== 'down' && s.target.footing !== 'yielded') {
    const angry = npc.truth.mood.mood === 'angry';
    const bravado = npc.flaw.kind === 'bravado' && s.witnesses.length > 0;
    let swing = 0.12 + 0.1 * (him.nerve - you.presence) + (s.physical ? 0.35 : 0) + (angry ? 0.15 : 0) + (bravado ? 0.15 : 0) + (s.hisTurf ? 0.1 : 0);
    if (s.target.footing === 'held') swing -= 0.2;
    if (s.intent.npc === 'hurt') swing += 0.2;
    const flee = s.target.footing === 'staggered' ? clamp01(0.15 + 0.1 * (3 - him.nerve)) : 0.02;

    if (rng.bool(clamp01(swing))) {
      // He came at you. If you were only leaning, it is a fight now, and
      // nobody at this door chose that.
      const swungFirst = !s.physical && s.intent.player === 'scare';
      escalate('npc', 'hurt', 'swung_first');
      if (swungFirst) escalate('player', 'hurt', 'swung_first');
      if (rng.bool(hitChance(him, you, false, s.player.footing))) landOn('player');
      else lines.push({ who: 'npc', act: 'swings_and_misses' });
    } else if (rng.bool(flee)) {
      s = { ...s, target: { ...s.target, footing: 'fled' } };
      lines.push({ who: 'npc', act: 'runs' });
      ended = true;
    } else if (s.target.footing === 'held') {
      if (rng.bool(clamp01(0.4 + 0.15 * (him.body - you.body)))) {
        s = { ...s, target: { ...s.target, footing: 'standing' } };
        lines.push({ who: 'npc', act: 'shakes_free' });
      }
    }
  }

  // --- what nobody chose ----------------------------------------------------
  // He's down and your hands are still moving. Intent escalates as
  // character, not dice (§17): a temper is a flaw you took.
  let keptGoing = false;
  if (s.target.footing === 'down' && state.player.flaws.some((f) => f.kind === 'temper' || f.kind === 'reckless')) {
    keptGoing = true;
    lines.push({ who: 'player', act: 'keeps_going' });
    escalate('player', 'hurt', 'temper');
    s = { ...s, target: { ...s.target, blows: s.target.blows + 1 } };
  }

  if (s.target.footing === 'down' || s.player.footing === 'down' || s.target.footing === 'fled') ended = true;
  if (!ended && s.beat >= s.maxBeats) {
    lines.push({ who: 'player', act: 'backs_off' }, { who: 'npc', act: 'backs_off' });
    ended = true;
  }

  s = { ...s, lines: [...s.lines, ...lines] };
  let next: WorldState = { ...state, scene: s, rng: rng.getState() };

  if (!ended) return { state: next, lines, end: null };

  const concluded = conclude(next, s, keptGoing);
  next = concluded.state;
  return { state: next, lines, end: concluded.end };
}

// ---------------------------------------------------------------------------
// Exit into outcomes (§14, §17)
// ---------------------------------------------------------------------------

function conclude(state: WorldState, s: ActiveScene, keptGoing: boolean): { readonly state: WorldState; readonly end: SceneEnd } {
  const rng = Rng.fromState(state.rng, state.seed);
  const became: SceneEnd['became'] = s.physical ? 'fistfight' : 'shakedown';
  const endedAs: Intent = s.physical ? 'hurt' : 'scare';
  const paid = s.lines.reduce((sum, l) => sum + (l.act === 'pays' ? l.amount : 0), 0);

  const won = s.target.footing === 'down' || s.target.footing === 'fled' || s.target.footing === 'yielded';
  const lost = s.player.footing === 'down' || s.player.footing === 'fled';
  const outcome = won ? 'success' : lost ? 'failure' : null;

  // The event. Witnesses and the target remember it forever; the grievance
  // is stored and not acted on (§13, ladder inactive in V0).
  const committed = commitEvent(state, {
    type: became,
    actor: 'player',
    target: { kind: 'npc', id: s.npc },
    location: s.location,
    witnesses: s.witnesses,
    outcome,
    intent: endedAs,
  });
  let next = committed.state;
  const eventId = committed.event.id;

  // Injuries: a state with a cost tail, on both of you.
  let targetInjury: string | null = null;
  let playerInjury: string | null = null;
  const hisSeverity = severityFromBlows(s.target.blows, keptGoing);
  if (hisSeverity) {
    const made = makeInjury(next, rng, hisSeverity, eventId, next.time);
    next = made.state;
    const hurt = next.npcs[s.npc]!;
    next = { ...next, npcs: { ...next.npcs, [s.npc]: { ...hurt, injuries: [...hurt.injuries, made.injury] } } };
    targetInjury = made.injury.site;
  }
  const yourSeverity = severityFromBlows(s.player.blows, false);
  if (yourSeverity) {
    const made = makeInjury(next, rng, yourSeverity, eventId, next.time);
    next = made.state;
    next = { ...next, player: { ...next.player, injuries: [...next.player.injuries, made.injury] } };
    playerInjury = made.injury.site;
  }

  // He is not the same toward you, and neither is anyone who watched.
  const fearDelta = s.physical ? TUNING.violence.fear.blows : TUNING.violence.fear.menace;
  const outcomes: Outcome[] = [
    {
      type: 'relationship_shift',
      target: s.npc,
      visible: true,
      source: 'sim',
      payload: { fear: fearDelta, warmth: TUNING.violence.warmth.target, trust: TUNING.violence.trust.target },
    },
    ...s.witnesses.map(
      (id): Outcome => ({
        type: 'relationship_shift',
        target: id,
        visible: true,
        source: 'sim',
        payload: { fear: TUNING.violence.fear.witness, warmth: TUNING.violence.warmth.witness, trust: TUNING.violence.trust.witness },
      }),
    ),
  ];
  if (paid > 0) {
    outcomes.push({
      type: 'transfer',
      target: s.npc,
      visible: true,
      source: 'sim',
      payload: { from: s.npc, to: 'player', what: { kind: 'money', amount: paid } },
    });
  }
  // Spend against whatever this encounter's beats have already put toward
  // the cap — §14 applies the same way here as it does to a conversation.
  // In V0 this is still the scene's only outcomes-bearing call (beats
  // themselves don't emit outcomes), but the budget lives on `ActiveScene`
  // so that stays true even if a later beat gains one.
  next = applyOutcomes(next, outcomes, { sceneEvent: eventId, counterpart: s.npc, budget: s.budget }).state;

  const shaken = next.npcs[s.npc]!;
  next = {
    ...next,
    npcs: {
      ...next.npcs,
      [s.npc]: { ...shaken, truth: { ...shaken.truth, mood: { mood: 'angry', intensity: s.physical ? 0.9 : 0.6 } }, lastResolved: next.time },
    },
    scene: null,
    rng: rng.getState(),
  };

  return {
    state: next,
    end: { kind: s.kind, event: eventId, became, startedAs: s.startedAs, endedAs, outcome, paid, playerInjury, targetInjury },
  };
}
